import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth';
import { useNotificationContext } from '@/features/notifications';
import {
  useInitiateConfluenceAuth,
  useGetConfluenceConnectionStatus,
  useDeleteConfluenceConnection,
} from '@/shared/api';

export const useConfluenceConnection = () => {
  const { accessToken } = useAuth();
  const { showNotification } = useNotificationContext();
  const queryClient = useQueryClient();

  const initiateConfluenceAuthMutation = useInitiateConfluenceAuth<{
    authUrl: string;
  }>({});
  const deleteConfluenceConnectionMutation = useDeleteConfluenceConnection();
  const { data, isFetching: confluenceIsLoading } =
    useGetConfluenceConnectionStatus<{ connected: boolean }>({});
  const confluenceConnected = data?.connected;

  const onConfluenceDisconnect = async () => {
    try {
      await deleteConfluenceConnectionMutation.mutateAsync({});
      queryClient.invalidateQueries(['confluence', 'connection-status']);
      showNotification({
        type: 'success',
        message: 'Success',
        description: 'Disconnected from Confluence successfully',
      });
    } catch (error) {
      showNotification({
        type: 'error',
        message: 'Error',
        description: 'Failed to disconnect from Confluence',
      });
    }
  };

  const onConfluenceConnect = async () => {
    try {
      const { data } = await initiateConfluenceAuthMutation.refetch();
      if (data?.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (error) {
      showNotification({
        type: 'error',
        message: 'Error',
        description: 'Failed to connect to Confluence',
      });
    }
  };

  useEffect(() => {
    queryClient.invalidateQueries(['confluence', 'connection-status']);
  }, [accessToken]);

  return {
    confluenceConnected,
    confluenceIsLoading,
    onConfluenceConnect,
    onConfluenceDisconnect,
  };
};
